// Read-receipt sender. Called by the chat-room host (web's
// `WebChatRoom`, desktop's equivalent) when the user opens a chat or
// the most-recent peer message scrolls into view. Emits a
// `com.ping.read` Application envelope back to the conversation so
// every other member (the original sender and any other readers'
// devices) can update the MessageBubble's `readBy` array → flip the
// glyph from single-check (delivered) to double-check (read).
//
// Why a hook + helper instead of inlining into the chat room: the
// signal can fire from multiple places — initial chat focus,
// scroll-to-bottom, switching from background to foreground, push-to-
// open. Centralising the encode + send keeps every entry point in
// sync about the payload shape.
//
// Wire-format note: per `docs/architecture/types-and-schemas.md` §4.2
// the doc-aligned home for `read` is the EphemeralFrame channel
// (typing/presence/read share an AES-256-GCM key from
// `exportConversationSecret("ping/ephemeral", ...)`). We use the
// Application-envelope path here because it ships TODAY without new
// BE routing / ephemeral nonce-seq management — same MLS encryption,
// same per-device fan-out, plus backfill-on-reconnect which the
// ephemeral channel doesn't have. The receiver-side handler in
// `useIncomingMessages.persistIncoming` is keyed on the
// `com.ping.read` discriminator, so a future migration to
// EphemeralFrame can swap transports without changing the payload
// contract.

import { log } from '../log/log';

interface ClientWithGetConversation {
  getConversation?(id: Uint8Array): { send(plaintext: Uint8Array): Promise<unknown> };
}

export interface SendReadReceiptOptions {
  readonly client: unknown;
  /** MLS group id of the conversation (16 bytes). The SDK's
   *  `client.send` takes raw bytes; the caller converts from
   *  hex / base64url as needed. */
  readonly conversationIdBytes: Uint8Array;
  /** Content hash (hex) of the most-recently-read peer message in
   *  this conversation. A single ack covers "I've seen up to and
   *  including this message" — receivers patch only the matching
   *  bubble, but the on-screen UX still reads as "everything above
   *  is read" because that's how the chat list orders things. */
  readonly contentHashHex: string;
  /** Optional wall-clock ms. Defaults to `Date.now()`. Exposed so
   *  hosts that need to stamp a specific moment (e.g. when a chat
   *  was opened in a notification tap) can override. */
  readonly readAt?: number;
}

/**
 * Fire a read receipt. Errors are swallowed — receipts are a UX
 * nicety, not a correctness invariant. The bubble stays at
 * "delivered" if the receipt round-trip fails; the next read-emit
 * (e.g. the user re-focuses the chat) gets another shot.
 */
export async function sendReadReceipt(
  opts: SendReadReceiptOptions,
): Promise<void> {
  const { client, conversationIdBytes, contentHashHex } = opts;
  const c = client as ClientWithGetConversation;
  if (typeof c.getConversation !== 'function') {
    log.debug('readReceipt.no_getConversation_method');
    return;
  }
  const readAt = opts.readAt ?? Date.now();
  const envelope = {
    kind: 'com.ping.read',
    payload: {
      messageContentHash: contentHashHex,
      readAt,
    },
  };
  const delays = [0, 800, 1600];
  let lastErr: unknown;
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (delays[attempt]! > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, delays[attempt]));
    }
    try {
      const conv = c.getConversation(conversationIdBytes);
      await conv.send(new TextEncoder().encode(JSON.stringify(envelope)));
      log.debug('readReceipt.sent', {
        contentHashPrefix: contentHashHex.slice(0, 8),
        attempt,
      });
      return;
    } catch (err) {
      lastErr = err;
      log.debug('readReceipt.send_failed', {
        contentHashPrefix: contentHashHex.slice(0, 8),
        attempt,
        error: (err as Error).message,
      });
    }
  }
  log.debug('readReceipt.send_exhausted', {
    contentHashPrefix: contentHashHex.slice(0, 8),
    error: (lastErr as Error | undefined)?.message,
  });
}
